import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { isAppInstalled, openAppForConnection, createConnection, saveConnectionToFirestore } from '../services/connectionService';
import { trackEvent } from '../tracking/pixels';

/**
 * ConnectPage Component
 * Handles QR code connection flow with conditional app detection
 */
export const ConnectPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'checking' | 'app_installed' | 'app_not_installed' | 'connecting' | 'connected' | 'error'>('checking');
  const [error, setError] = useState<string | null>(null);
  const scannedUserID = searchParams.get('id');

  const handleAppInstalledConnection = async () => {
    try {
      setStatus('connecting');
      
      // Get current user ID (from auth or localStorage)
      const scannerUserID = localStorage.getItem('userID') || 'anonymous';
      
      if (scannerUserID === 'anonymous') {
        // If no user ID, try to open app for authentication
        openAppForConnection(scannedUserID!);
        return;
      }

      // Create connection via Octopus API
      await createConnection(scannerUserID, scannedUserID!);
      
      // Save to Firestore
      await saveConnectionToFirestore(scannerUserID, scannedUserID!);
      
      setStatus('connected');
      trackEvent('qr_connect_success', { scannedUserID: scannedUserID! });
      
      // Redirect to app after 2 seconds
      setTimeout(() => {
        openAppForConnection(scannedUserID!);
      }, 2000);
    } catch (error) {
      console.error('Connection error:', error);
      setStatus('error');
      setError(String(error));
      trackEvent('qr_connect_error', { error: String(error) });
    }
  };

  useEffect(() => {
    if (!scannedUserID) {
      setStatus('error');
      setError('Invalid connection link');
      return;
    }

    // Check if app is installed
    const appInstalled = isAppInstalled();
    
    if (appInstalled) {
      handleAppInstalledConnection();
    } else {
      setStatus('app_not_installed');
      trackEvent('qr_connect_app_not_installed', { scannedUserID });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedUserID]);

  const handleInstallApp = () => {
    if (scannedUserID) {
      trackEvent('qr_connect_install_cta', { scannedUserID });
      openAppForConnection(scannedUserID);
    }
  };

  if (!scannedUserID) {
    return (
      <div className="connect-page">
        <div className="connect-container">
          <div className="connect-content">
            <h1>Invalid Connection Link</h1>
            <p>The connection link is invalid or missing a user ID.</p>
            <button onClick={() => navigate('/')}>Return to Home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="connect-page">
      <div className="connect-container">
        <div className="connect-content">
          {status === 'checking' && (
            <>
              <div className="connect-loader">⏳</div>
              <h1>Checking connection...</h1>
            </>
          )}

          {status === 'app_installed' && (
            <>
              <div className="connect-success">✅</div>
              <h1>Connecting...</h1>
              <p>Opening Gig Genie app to complete connection.</p>
            </>
          )}

          {status === 'app_not_installed' && (
            <>
              <div className="connect-icon">📱</div>
              <h1>Install Gig Genie to Connect</h1>
              <p>
                To connect with this gig worker, you'll need the Gig Genie app. 
                After installing, scan the QR code again to complete the connection.
              </p>
              <button className="connect-install-button" onClick={handleInstallApp}>
                Install Gig Genie
              </button>
              <p className="connect-note">
                Already installed? <a href={`giggenie://connect?userID=${scannedUserID}`}>Open App</a>
              </p>
            </>
          )}

          {status === 'connecting' && (
            <>
              <div className="connect-loader">⏳</div>
              <h1>Connecting...</h1>
              <p>Creating connection in Gig Connect...</p>
            </>
          )}

          {status === 'connected' && (
            <>
              <div className="connect-success">✅</div>
              <h1>Connected!</h1>
              <p>You've successfully connected with this gig worker.</p>
              <p className="connect-redirect">Opening Gig Genie app...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="connect-error">⚠️</div>
              <h1>Connection Failed</h1>
              <p>{error || 'An error occurred while processing the connection.'}</p>
              <button onClick={() => navigate('/')}>Return to Home</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

